import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link, useLocation } from "react-router-dom";
import { Dropdown, Menu, Button, Badge } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import defaultImage from "../../Global Icons/default_image.png";
import { viewCategoryList } from "../../Redux/Actions/categoryActions";
import TableComponent from "../../Global Functions/tableComponents";

const ViewCategory = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const queryParams = new URLSearchParams(location.search);
  const clickedCategoryName = queryParams.get("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const { id: fetchedId } = useParams();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

  const fetchDetails = useCallback(async (page, pageSize) => {
    const subCategoriesData = {
      page,
      limit: pageSize,
      category_id: fetchedId,
      sort: { createdAt: -1 },
      filter: debouncedSearch,
      ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
      ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
    };
    try {
      await dispatch(viewCategoryList(subCategoriesData));
    } catch {
      toast.error("Failed to fetch projects");
    }
  },
    [dispatch, debouncedSearch, dateRange, fetchedId]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);


  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedId, pagination.current, pagination.pageSize, debouncedSearch, dateRange, fetchDetails]);

  const { categories } = useSelector((state) => state.categories);

  useEffect(() => {
    if (categories?.total) {
      setPagination((prevData) => ({ ...prevData, total: categories.total }));
    }
  }, [categories]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDateRangeApply = (start, end) => {
    setDateRange(start === null && end === null ? { start: null, end: null } : { start, end });
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [debouncedSearch]);

  const getDropDownMenu = (record) => (
    <Menu>
      <Menu.Item key="view">
        <Link to={`/categories/sub/sub/view/${record._id}?name=${encodeURIComponent(record.name)}`}>
          View
        </Link>
      </Menu.Item>
      {canEdit && (
        <Menu.Item key="edit">
          <Link to={`/categories/view/sub/edit/${record._id}`}>Edit</Link>
        </Menu.Item>
      )}
    </Menu >
  );

  const columns = [
    {
      title: "Sr No.",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Type",
      dataIndex: "business_type",
      align: "center",
      key: "business_type",
      render: (business_type) =>
        Array.isArray(business_type) && business_type.length > 0
          ? business_type.join(", ")
          : "N/A",
    },
    {
      title: "Sub - Category Name",
      dataIndex: "name",
      align: "center",
      key: "name",
    },
    {
      title: "Image",
      align: "center",
      dataIndex: "image",
      key: "image",
      render: (image, record) => (
        <img
          key={record._id}
          src={image || defaultImage}
          alt="No Data"
          decoding="async"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
          style={{ borderRadius: "20px", height: "50px", width: "50px" }}
        />
      ),
    },
    {
      title: "Icon",
      align: "center",
      dataIndex: "icon",
      key: "icon",
      render: (image, record) => (
        <img
          key={record._id}
          src={image || defaultImage}
          alt="No Data"
          decoding="async"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
          style={{ borderRadius: "20px", height: "50px", width: "50px" }}
        />
      ),
    },
    {
      title: "3D Icon",
      align: "center",
      dataIndex: "three_d_icon",
      key: "three_d_icon",
      render: (image, record) => (
        <img
          key={record._id}
          src={image || defaultImage}
          alt="No Data"
          decoding="async"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
          style={{ borderRadius: "20px", height: "50px", width: "50px" }}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 8px",
            border: "1px solid #d9d9d9",
            borderRadius: "10px",
          }}
        >
          <Badge
            color={status === "active" ? "green" : "red"}
            text={status.charAt(0).toUpperCase() + status.slice(1)}
            background
          />
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Dropdown overlay={getDropDownMenu(record)} trigger={["click"]}>
          <Button shape="square" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <TableComponent
        h3heading={clickedCategoryName ? clickedCategoryName : "No Data"}
        buttonhead2={canAdd ? "Add Sub Category" : null}
        navigateTo2={canAdd ? `/categories/${fetchedId}/sub/add` : null}
        buttonhead="Go Back"
        setState={setSearchQuery}
        navigateTo={`/categories`}
        columns={columns}
        dataSource={(Array.isArray(categories?.data) && categories.data) || []}
        rowKey="_id"
        pagination={pagination}
        onCalendarClick={() => setIsDatePopupVisible(true)}
        onChange={handleTableChange}
      ></TableComponent>


      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>

  );
};

export default ViewCategory;
